import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Autocomplete,
  Typography,
  Slide,
  Tooltip,
  Box,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import { CREATE_MANUAL_ASSET } from "../../apis/settingsApis/assets-apis/allAssetsApis";
import { GET_STAFF_DETAILS } from "../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../components/Snackbar/ConfirmSnackbar";

const TransitionComponent = (props) => <Slide {...props} direction="left" />;

const DEVICE_TYPES = [
  { value: "laptop", label: "Laptop" },
  { value: "desktop", label: "Desktop" },
  { value: "router", label: "Router" },
  { value: "phone", label: "Phone" },
  { value: "tablet", label: "Tablet" },
  { value: "server", label: "Server" },
  { value: "other", label: "Other" },
];

const OPERATING_SYSTEMS = [
  { value: "macOS", label: "macOS" },
  { value: "Windows", label: "Windows" },
  { value: "Linux", label: "Linux" },
  { value: "iOS", label: "iOS" },
  { value: "Android", label: "Android" },
  { value: "Other", label: "Other" },
];

const OS_BY_CATEGORY = {
  computer: OPERATING_SYSTEMS.filter((o) => ["macOS", "Windows", "Linux", "Other"].includes(o.value)),
  mobile: OPERATING_SYSTEMS.filter((o) => ["iOS", "Android", "Other"].includes(o.value)),
  server: OPERATING_SYSTEMS.filter((o) => ["Linux", "Windows", "macOS", "Other"].includes(o.value)),
  all: OPERATING_SYSTEMS,
};

// Per device type: which optional fields apply and whether OS is required.
// Routers are network appliances — no desktop/mobile OS to report.
const DEVICE_FIELD_RULES = {
  laptop: { osRequired: true, osOptions: "computer", showHostname: true, showModel: true },
  desktop: { osRequired: true, osOptions: "computer", showHostname: true, showModel: true },
  phone: { osRequired: true, osOptions: "mobile", showHostname: false, showModel: true },
  tablet: { osRequired: true, osOptions: "mobile", showHostname: false, showModel: true },
  server: { osRequired: true, osOptions: "server", showHostname: true, showModel: true },
  router: { showOs: false, showHostname: true, showModel: true },
  other: { osRequired: false, osOptions: "all", showHostname: true, showModel: true },
};

const getDeviceRules = (deviceType) => {
  const rules = DEVICE_FIELD_RULES[deviceType] || DEVICE_FIELD_RULES.other;
  const osOptionsKey = rules.osOptions || "all";
  return {
    ...rules,
    showOs: rules.showOs !== false,
    osOptionsList: OS_BY_CATEGORY[osOptionsKey] || OPERATING_SYSTEMS,
  };
};

const FIELD_HINTS = {
  name: "A friendly label for this device in the dashboard, e.g. \"Sahil's MacBook\".",
  serialNumber: "The manufacturer's unique serial number — usually on a device label or in system settings.",
  hostname: "The network name used to identify this device on your local network, e.g. \"sahil-mbp.local\".",
  os: "The operating system installed on this device.",
  osVersion: "The OS version number, e.g. \"14.5\" for macOS or \"11\" for Windows.",
  model: "The hardware model, e.g. \"MacBook Pro\" or \"ThinkPad X1 Carbon\".",
  disposableDate: "When this device is planned for retirement. After this date it moves out of scope.",
  description: "Optional notes — location, purpose, or any other context for this device.",
};

const DEFAULT_PLACEHOLDERS = {
  name: "Enter a device name",
  serialNumber: "Enter serial number",
  hostname: "e.g., hostname.local",
  osVersion: "e.g., 14.5",
  model: "e.g., Device model",
  description: "Add a note about this device…",
};

const PLACEHOLDERS_BY_DEVICE = {
  laptop: {
    name: "e.g., Sahil's MacBook",
    serialNumber: "e.g., FVFF9BWKQ05D",
    hostname: "e.g., sahil-mbp.local",
    model: "e.g., MacBook Pro 14\"",
    description: "e.g., Assigned laptop for daily work",
  },
  desktop: {
    name: "e.g., Office Workstation 03",
    serialNumber: "e.g., PC12345678",
    hostname: "e.g., ws-hr-03.corp.local",
    model: "e.g., Dell OptiPlex 7090",
    description: "e.g., Shared HR department desktop",
  },
  router: {
    name: "e.g., Floor 2 WiFi Router",
    serialNumber: "e.g., SN on device label",
    hostname: "e.g., router-floor2.local",
    model: "e.g., Cisco Meraki MR46",
    description: "e.g., Office WiFi — floor 2 east wing",
  },
  phone: {
    name: "e.g., Sahil's iPhone",
    serialNumber: "e.g., IMEI or serial from Settings",
    model: "e.g., iPhone 15 Pro",
    description: "e.g., Company-issued work phone",
  },
  tablet: {
    name: "e.g., Sahil's iPad",
    serialNumber: "e.g., Serial from Settings → General",
    model: "e.g., iPad Air",
    description: "e.g., Field team tablet",
  },
  server: {
    name: "e.g., prod-api-01",
    serialNumber: "e.g., Dell service tag",
    hostname: "e.g., prod-api-01.corp.local",
    model: "e.g., Dell PowerEdge R740",
    description: "e.g., Production API — rack A3",
  },
  other: {
    name: "e.g., Conference Room Display",
    serialNumber: "e.g., Serial on device label",
    hostname: "e.g., device-hostname.local",
    model: "e.g., Samsung QM65R",
    description: "e.g., Any additional context",
  },
};

const OS_VERSION_PLACEHOLDERS = {
  macOS: "e.g., 14.5",
  Windows: "e.g., 11 (build 22631)",
  Linux: "e.g., 22.04",
  iOS: "e.g., 17.4",
  Android: "e.g., 14",
  Other: "e.g., Version number",
};

const getPlaceholders = (deviceType, os) => ({
  ...DEFAULT_PLACEHOLDERS,
  ...(PLACEHOLDERS_BY_DEVICE[deviceType] || {}),
  osVersion: OS_VERSION_PLACEHOLDERS[os] || DEFAULT_PLACEHOLDERS.osVersion,
});

const EMPTY_FORM = {
  name: "",
  serialNumber: "",
  ownerEmployeeId: "",
  deviceType: "",
  hostname: "",
  os: "",
  osVersion: "",
  model: "",
  disposableDate: "",
  description: "",
};

const AddAssetDialog = ({ open, onClose, refetch }) => {
  const theme = useTheme();
  const [createManualAsset, { loading }] = useMutation(CREATE_MANUAL_ASSET);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // Owner options come from the people list (ownerEmployeeId = employee_data_id, a UUID).
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS, {
    skip: !open,
    fetchPolicy: "cache-first",
  });

  const ownerOptions = useMemo(() => {
    if (!staffData?.getEmployees?.data) return [];
    return staffData.getEmployees.data
      .filter((e) => e.in_scope !== false)
      .map((e) => ({
        id: e.employee_data_id,
        name: e.name || `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        email:
          e.work_email ||
          e.emails?.find((em) => em.type === "work")?.email ||
          e.emails?.find((em) => em.is_primary)?.email ||
          e.emails?.[0]?.email ||
          "",
      }));
  }, [staffData]);

  const selectedOwner = useMemo(
    () => ownerOptions.find((o) => o.id === formData.ownerEmployeeId) || null,
    [ownerOptions, formData.ownerEmployeeId]
  );

  const deviceRules = useMemo(
    () => getDeviceRules(formData.deviceType),
    [formData.deviceType]
  );

  const placeholders = useMemo(
    () => getPlaceholders(formData.deviceType, formData.os),
    [formData.deviceType, formData.os]
  );

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDeviceTypeChange = (e) => {
    const deviceType = e.target.value;
    const rules = getDeviceRules(deviceType);

    setFormData((prev) => {
      const next = { ...prev, deviceType };
      if (!rules.showOs) {
        next.os = "";
        next.osVersion = "";
      } else if (prev.os && !rules.osOptionsList.some((o) => o.value === prev.os)) {
        next.os = "";
        next.osVersion = "";
      }
      if (!rules.showHostname) next.hostname = "";
      return next;
    });

    setErrors((prev) => {
      const next = { ...prev, deviceType: "" };
      if (!rules.showOs) {
        next.os = "";
        next.osVersion = "";
      }
      if (!rules.showHostname) next.hostname = "";
      return next;
    });
  };

  const validate = () => {
    const next = {};
    const rules = getDeviceRules(formData.deviceType);

    if (!formData.name?.trim()) next.name = "Required";
    if (!formData.serialNumber?.trim()) next.serialNumber = "Required";
    if (!formData.ownerEmployeeId) next.ownerEmployeeId = "Required";
    if (!formData.deviceType) next.deviceType = "Required";
    if (rules.showOs && rules.osRequired && !formData.os) next.os = "Required";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  const notify = (message) =>
    setSnackbarInfo({
      message,
      vertical: "top",
      horizontal: "right",
      Transition: TransitionComponent,
    });

  const handleSubmit = async () => {
    if (!validate()) return;
    const rules = getDeviceRules(formData.deviceType);
    try {
      const response = await createManualAsset({
        variables: {
          name: formData.name.trim(),
          serialNumber: formData.serialNumber.trim(),
          ownerEmployeeId: formData.ownerEmployeeId,
          deviceType: formData.deviceType,
          hostname: deviceRules.showHostname ? (formData.hostname?.trim() || null) : null,
          os: rules.showOs ? (formData.os || null) : null,
          osVersion: rules.showOs ? (formData.osVersion?.trim() || null) : null,
          model: deviceRules.showModel ? (formData.model?.trim() || null) : null,
          description: formData.description?.trim() || null,
          // Date input is "YYYY-MM-DD"; the API expects an ISO datetime.
          disposableDate: formData.disposableDate
            ? `${formData.disposableDate}T00:00:00Z`
            : null,
        },
      });

      if (response?.data?.createManualAsset?.success) {
        notify(response.data.createManualAsset.message || "Asset added");
        handleClose();
        if (refetch) refetch();
      } else {
        notify(response?.data?.createManualAsset?.message || "Failed to add asset");
      }
    } catch (error) {
      console.error("Error creating asset:", error);
      notify(error?.message || "Failed to add asset");
    }
  };

  const labelSx = {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.customColors?.text?.strong ?? "#374151",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      "&:hover fieldset": { borderColor: theme.customColors.border.strong },
    },
  };

  const renderLabel = (label, { required, hint } = {}) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography component="span" sx={labelSx}>
        {label} {required && "*"}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <InfoOutlinedIcon
            fontSize="small"
            sx={{
              fontSize: 16,
              color: theme.customColors?.text?.secondary ?? "#6B7280",
              cursor: "help",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );

  // Render helper (called inline, NOT used as a <Component/>, so the inputs keep
  // focus across re-renders). Keeps every field visually consistent, like the
  // People form.
  const renderField = ({
    label,
    field,
    required,
    select,
    type,
    placeholder,
    options,
    sm = 6,
    multiline,
    hint,
    onChange,
  }) => (
    <Grid item xs={12} sm={sm}>
      {renderLabel(label, { required, hint })}
      <TextField
        fullWidth
        select={select}
        type={type}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        placeholder={placeholder}
        value={formData[field]}
        onChange={onChange || handleChange(field)}
        error={!!errors[field]}
        helperText={errors[field]}
        size="small"
        sx={inputSx}
      >
        {select && [
          <MenuItem key="__none" value="">None</MenuItem>,
          ...options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          )),
        ]}
      </TextField>
    </Grid>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2.5,
            px: 3,
            position: "relative",
          }}
        >
          Add Asset
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: theme.customColors.text.secondary,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ px: 3, py: 3, "&.MuiDialogContent-root": { paddingTop: 1 } }}
        >
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            {renderField({
              label: "Device Name",
              field: "name",
              required: true,
              placeholder: placeholders.name,
              hint: FIELD_HINTS.name,
            })}
            {renderField({
              label: "Serial Number",
              field: "serialNumber",
              required: true,
              placeholder: placeholders.serialNumber,
              hint: FIELD_HINTS.serialNumber,
            })}
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 0.75 }}>
                <Typography component="label" sx={labelSx}>Owner *</Typography>
              </Box>
              <Autocomplete
                options={ownerOptions}
                loading={staffLoading}
                value={selectedOwner}
                onChange={(e, option) => {
                  setFormData((prev) => ({ ...prev, ownerEmployeeId: option?.id || "" }));
                  if (errors.ownerEmployeeId) setErrors((prev) => ({ ...prev, ownerEmployeeId: "" }));
                }}
                getOptionLabel={(o) => (o?.email ? `${o.name} (${o.email})` : o?.name || "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select owner"
                    error={!!errors.ownerEmployeeId}
                    helperText={errors.ownerEmployeeId}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            {renderField({
              label: "Device Type",
              field: "deviceType",
              required: true,
              select: true,
              options: DEVICE_TYPES,
              onChange: handleDeviceTypeChange,
            })}
            {!!formData.deviceType && deviceRules.showHostname && renderField({
              label: "Hostname",
              field: "hostname",
              placeholder: placeholders.hostname,
              hint: FIELD_HINTS.hostname,
            })}
            {!!formData.deviceType && deviceRules.showOs && renderField({
              label: "Operating System",
              field: "os",
              required: deviceRules.osRequired,
              select: true,
              options: deviceRules.osOptionsList,
              hint: FIELD_HINTS.os,
            })}
            {!!formData.deviceType && deviceRules.showOs && renderField({
              label: "OS Version",
              field: "osVersion",
              placeholder: placeholders.osVersion,
              hint: FIELD_HINTS.osVersion,
            })}
            {!!formData.deviceType && deviceRules.showModel && renderField({
              label: "Model",
              field: "model",
              placeholder: placeholders.model,
              hint: FIELD_HINTS.model,
            })}
            {renderField({
              label: "Disposal Date",
              field: "disposableDate",
              type: "date",
              hint: FIELD_HINTS.disposableDate,
            })}
            {renderField({
              label: "Description",
              field: "description",
              placeholder: placeholders.description,
              sm: 12,
              multiline: true,
              hint: FIELD_HINTS.description,
            })}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 3,
            pb: 2.5,
            pt: 2,
            background: theme.customColors.background.cardHeader,
            borderTop: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={loading}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              bgcolor: theme.customColors.button.hover,
              color: theme.customColors.button.text,
              "&:hover": { bgcolor: theme.customColors.button.primary },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            Add Asset
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AddAssetDialog;
